import { useEffect, useRef, useState, type ReactNode } from 'react';

export const FADE_MS = 220;

type BodyMode = 'skeleton' | 'rows' | 'empty';

function resolveMode({ isLoading, hasData, hasCache, hasSettled }: { isLoading: boolean; hasData: boolean; hasCache: boolean; hasSettled: boolean }): BodyMode {
  if (hasData || (isLoading && hasCache)) return 'rows';
  if (isLoading || !hasSettled) return 'skeleton';
  return 'empty';
}

function useTableBodyFade<TRow, TData>({ isLoading = false, rows, data }: { isLoading?: boolean; rows?: TRow[]; data?: TData[] }) {
  const [cachedRows, setCachedRows] = useState(rows);
  const [cachedData, setCachedData] = useState(data);
  const hasData = Array.isArray(rows) && rows.length > 0;
  const hasCache = Array.isArray(cachedRows) && cachedRows.length > 0;
  const wasLoading = useRef(isLoading);
  const [hasSettled, setHasSettled] = useState(() => hasData && !isLoading);
  const targetMode = resolveMode({ isLoading, hasData, hasCache, hasSettled });
  const [mode, setMode] = useState<BodyMode>(targetMode);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if ((wasLoading.current && !isLoading) || (hasData && !isLoading)) {
      setHasSettled(true);
    }
    wasLoading.current = isLoading;
  }, [isLoading, hasData]);

  useEffect(() => {
    if (hasData || !isLoading) {
      setCachedRows(rows);
      setCachedData(data);
    }
  }, [hasData, rows, data, isLoading]);

  useEffect(() => {
    if (targetMode === mode) {
      if (isLoading && mode === 'rows') {
        setVisible(false);
        return;
      }
      if (!isLoading) {
        const id = requestAnimationFrame(() => {
          requestAnimationFrame(() => setVisible(true));
        });
        return () => cancelAnimationFrame(id);
      }
      return;
    }

    setVisible(false);
    const timeout = window.setTimeout(() => setMode(targetMode), FADE_MS);
    return () => window.clearTimeout(timeout);
  }, [targetMode, mode, isLoading]);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setVisible(true));
    });
    return () => cancelAnimationFrame(id);
  }, [mode]);

  return { mode, visible, cachedRows, cachedData };
}

export default function TableBodyFade<TRow, TData>({
  isLoading = false,
  rows,
  data,
  skeleton,
  empty,
  children,
}: {
  isLoading?: boolean;
  rows?: TRow[];
  data?: TData[];
  skeleton: ReactNode;
  empty: ReactNode;
  children: (rows: TRow[], data: TData[]) => ReactNode;
}) {
  const { mode, visible, cachedRows, cachedData } = useTableBodyFade({ isLoading, rows, data });

  return (
    <tbody
      aria-busy={isLoading || mode === 'skeleton'}
      className={`transition-opacity ease-out ${visible ? '' : 'pointer-events-none'}`}
      style={{
        opacity: visible ? 1 : 0,
        transitionDuration: `${FADE_MS}ms`,
      }}
    >
      {mode === 'rows' && cachedRows && cachedRows.length > 0 ? children(cachedRows, cachedData ?? []) : mode === 'empty' ? empty : skeleton}
    </tbody>
  );
}
